import { useLocation, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SEOHead } from "@/hooks/useSEO";
import seoConfig from "@/config/seo.json";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  createWebPageSchema,
  createArticleSchema,
} from "@/hooks/useStructuredData";
import { getCaseStudyPageBySlug } from "@/data/caseStudyPages";
import { useLanguage } from "@/hooks/useLanguage";

import CaseStudyHero from "@/components/landing/case-studies/CaseStudyHero";
import CaseStudyStats from "@/components/landing/case-studies/CaseStudyStats";
import CaseStudyOverview from "@/components/landing/case-studies/CaseStudyOverview";
import CaseStudyChallenge from "@/components/landing/case-studies/CaseStudyChallenge";
import CaseStudySolution from "@/components/landing/case-studies/CaseStudySolution";
import CaseStudyResults from "@/components/landing/case-studies/CaseStudyResults";
import CaseStudyQuote from "@/components/landing/case-studies/CaseStudyQuote";
import CTASection from "@/components/landing/CTASection";

import VerbaendeCaseStudyHero from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyHero";
import VerbaendeCaseStudyStats from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyStats";
import VerbaendeCaseStudyOverview from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyOverview";
import VerbaendeCaseStudyChallenge from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyChallenge";
import VerbaendeCaseStudySolution from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudySolution";
import VerbaendeCaseStudyResults from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyResults";
import VerbaendeCaseStudyQuote from "@/components/landing/case-studies/verbaende/VerbaendeCaseStudyQuote";

import MarketingCaseStudyHero from "@/components/landing/case-studies/marketing/MarketingCaseStudyHero";
import MarketingCaseStudyStats from "@/components/landing/case-studies/marketing/MarketingCaseStudyStats";
import MarketingCaseStudyOverview from "@/components/landing/case-studies/marketing/MarketingCaseStudyOverview";
import MarketingCaseStudyChallenge from "@/components/landing/case-studies/marketing/MarketingCaseStudyChallenge";
import MarketingCaseStudySolution from "@/components/landing/case-studies/marketing/MarketingCaseStudySolution";
import MarketingCaseStudyResults from "@/components/landing/case-studies/marketing/MarketingCaseStudyResults";
import MarketingCaseStudyQuote from "@/components/landing/case-studies/marketing/MarketingCaseStudyQuote";

import VertriebCaseStudyHero from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyHero";
import VertriebCaseStudyStats from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyStats";
import VertriebCaseStudyOverview from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyOverview";
import VertriebCaseStudyChallenge from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyChallenge";
import VertriebCaseStudySolution from "@/components/landing/case-studies/vertrieb/VertriebCaseStudySolution";
import VertriebCaseStudyResults from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyResults";
import VertriebCaseStudyQuote from "@/components/landing/case-studies/vertrieb/VertriebCaseStudyQuote";

type PageKey = keyof typeof seoConfig.pages;

const CaseStudyDetail = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const basePath = location.pathname.startsWith('/en/') ? location.pathname.slice(3) : location.pathname;
  const slug = basePath.replace("/case-studies/", "");
  const pageData = slug ? getCaseStudyPageBySlug(slug) : undefined;

  if (!pageData) {
    return <Navigate to="/404" replace />;
  }

  const renderContent = () => {
    switch (pageData.componentSet) {
      case "big-green-egg-head":
        return (<><CaseStudyHero /><CaseStudyStats /><CaseStudyOverview /><CaseStudyChallenge /><CaseStudySolution /><CaseStudyResults /><CaseStudyQuote /><CTASection /></>);
      case "verbaende-regionalverband":
        return (<><VerbaendeCaseStudyHero /><VerbaendeCaseStudyStats /><VerbaendeCaseStudyOverview /><VerbaendeCaseStudyChallenge /><VerbaendeCaseStudySolution /><VerbaendeCaseStudyResults /><CTASection /></>);
      case "marketing-2hm":
        return (<><MarketingCaseStudyHero /><MarketingCaseStudyStats /><MarketingCaseStudyOverview /><MarketingCaseStudyChallenge /><MarketingCaseStudySolution /><MarketingCaseStudyResults /><MarketingCaseStudyQuote /><CTASection /></>);
      case "vertrieb-ihr-hoergeraet":
        return (<><VertriebCaseStudyHero /><VertriebCaseStudyStats /><VertriebCaseStudyOverview /><VertriebCaseStudyChallenge /><VertriebCaseStudySolution /><VertriebCaseStudyResults /><VertriebCaseStudyQuote /><CTASection /></>);
      default:
        return <Navigate to="/404" replace />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page={(pageData.seoKey || "home") as PageKey} locale={language} />
      <StructuredDataHead schemas={[
        organizationSchema,
        createWebPageSchema(
          pageData.title,
          pageData.description,
          `/case-studies/${pageData.slug}`,
          [
            { name: "Home", url: "/" },
            { name: "Case Studies", url: "/" },
            { name: pageData.breadcrumbLabel, url: `/case-studies/${pageData.slug}` },
          ]
        ),
        createArticleSchema({
          title: pageData.title,
          description: pageData.description,
          slug: `case-studies/${pageData.slug}`,
          publishDate: pageData.publishDate,
          author: { name: pageData.author },
          category: pageData.category,
        }),
      ]} />
      <LLMMetadataHead
        title={pageData.title}
        description={pageData.description}
        keywords={pageData.keywords}
        canonicalUrl={pageData.canonicalUrl}
        aiCategory="Case Study, Erfolgsgeschichte, Referenz"
        aiEntityType="Case Study"
        aiIndustry={pageData.category}
      />
      <Header />
      <main>{renderContent()}</main>
      <Footer />
    </div>
  );
};

export default CaseStudyDetail;
